import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, HostListener, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import type { IColumn } from '@coreui/angular-pro';
import {
  AlertModule,
  BadgeComponent,
  ButtonCloseDirective,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ColComponent,
  ContainerComponent,
  ModalModule,
  RowComponent,
  SmartTableModule,
  SpinnerModule,
  TableModule
} from '@coreui/angular-pro';
import {
  cilCart,
  cilCloudDownload,
  cilCopy,
  cilExternalLink,
  cilFile,
  cilInfo,
  cilReload
} from '@coreui/icons';
import { IconDirective, IconModule, IconSetService } from '@coreui/icons-angular';
import { FeatureFlagsService } from 'app';
import { AuthService } from 'auth';
import { I18nService, TranslatePipe, TranslateService } from 'i18n';
import { isAdmin as isAdminUser, DownloadService, Logger } from 'shared';

interface Order {
  id: number;
  payment_method: string;
  status: string;
  total_price: string;
  payment_costs?: string;
  grand_total?: string;
  currency?: string;
  items_count: number;
  created_at: string;
  updated_at: string;
  web3_transaction_hash?: string;
  web3_confirmations?: number;
  web3_verified_at?: string;
  order_details?: OrderDetail[];
}

interface OrderDetail {
  id: number;
  product_id: number;
  product_name: string;
  quantity: number;
  price: string;
  subtotal: string;
}

interface OrdersResponse {
  orders: Order[];
  total_count: number;
  page: number;
  per_page: number;
  total_pages: number;
}

@Component({
  selector: 'app-user-orders-page',
  standalone: true,
  imports: [
    CommonModule,
    AlertModule,
    BadgeComponent,
    ButtonDirective,
    ButtonCloseDirective,
    CardBodyComponent,
    CardComponent,
    CardHeaderComponent,
    ColComponent,
    ContainerComponent,
    RowComponent,
    ModalModule,
    SmartTableModule,
    SpinnerModule,
    TableModule,
    IconDirective,
    IconModule,
    TranslatePipe
  ],
  templateUrl: './user-orders-page.component.html',
  styleUrls: ['./user-orders-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserOrdersPageComponent {
  private http = inject(HttpClient);
  private router = inject(Router);
  protected logger = new Logger('UserOrdersPageComponent');
  private iconSet = inject(IconSetService);
  private translateService = inject(TranslateService);
  private sanitizer = inject(DomSanitizer);
  private featureFlagsService = inject(FeatureFlagsService);
  private authService = inject(AuthService);
  private i18nService = inject(I18nService);
  private downloadService = inject(DownloadService);
  private elementRef: any = inject(ElementRef);
  private destroyRef = inject(DestroyRef);

  readonly Math = Math;
  readonly parseFloat = parseFloat;

  // ────────────────────────────────────────────────
  // Reactive state
  // ────────────────────────────────────────────────
  orders = signal<Order[]>([]);
  loading = signal<boolean>(false);
  totalCount = signal<number>(0);
  totalPages = signal<number>(0);
  unreadCount = signal<number>(0);

  // Pagination
  currentPage = signal<number>(1);
  itemsPerPage = signal<number>(20);

  // Feature flags & role
  ordersDisabled = computed(() => !this.featureFlagsService.isOrdersEnabled() && !this.isAdmin());
  isAdmin = computed(() => isAdminUser(this.authService.getUser()));

  // Modals
  detailsModalVisible = signal<boolean>(false);
  selectedOrder = signal<Order | null>(null);

  billPreviewModalVisible = signal<boolean>(false);
  billPreviewUrl = signal<SafeResourceUrl | null>(null);

  // Columns (reactive to language changes)
  columns = computed<IColumn[]>(() => [
    { key: 'id', label: this.translateService.translate('my_orders_order_id') },
    { key: 'created_at', label: this.translateService.translate('my_orders_order_date') },
    { key: 'payment_method', label: this.translateService.translate('my_orders_payment_method') },
    { key: 'status', label: this.translateService.translate('my_orders_status') },
    { key: 'total_price', label: this.translateService.translate('my_orders_total') },
    { key: 'items_count', label: this.translateService.translate('my_orders_items') },
    {
      key: 'controls',
      label: '',
      sorter: false,
      filter: false,
      _style: { width: '60px', textAlign: 'center' }
    }
  ]);

  pageNumbers = computed<number[]>(() => {
    const total = this.totalPages();
    const current = this.currentPage();
    const range = 2;
    const pages: number[] = [];
    for (let i = Math.max(1, current - range); i <= Math.min(total, current + range); i++) {
      pages.push(i);
    }
    return pages;
  });
  
  hasPaymentCosts = computed(() => {
    const costs = this.selectedOrder()?.payment_costs;
    return costs ? parseFloat(costs) > 0 : false;
  });

  // ────────────────────────────────────────────────
  // Effects + Initialization
  // ────────────────────────────────────────────────
  constructor() {
    this.iconSet.icons = {
      ...this.iconSet.icons,
      cilInfo,
      cilCart,
      cilCopy,
      cilExternalLink,
      cilReload,
      cilCloudDownload,
      cilFile
    };

    // Reload on view/page/language change
    effect(() => {
      this.currentPage();
      this.columns(); // triggers on language change
      this.loadOrders();
    });

    // Initial load
    this.loadOrders();
  }

  // ────────────────────────────────────────────────
  // Core actions
  // ────────────────────────────────────────────────
  loadOrders(): void {
    this.loading.set(true);

    const params = {
      page: this.currentPage().toString(),
      per_page: this.itemsPerPage().toString()
    };

    this.http.get<OrdersResponse>('/orders', { params })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          const transformed = response.orders.map(order => ({
            ...order,
            payment_method: this.formatPaymentMethod(order.payment_method),
            status: this.formatStatus(order.status),
            total_price: this.formatCurrency(parseFloat(order.total_price), order.currency),
            created_at: this.formatDateTime(order.created_at),
            _cellProps: {
              status: { class: this.getStatusClass(order.status) }
            }
          }));

          this.orders.set(transformed);
          this.totalCount.set(response.total_count);
          this.totalPages.set(response.total_pages);
          this.loading.set(false);
          this.logger.debug('Orders loaded:', response.orders.length);
        },
        error: err => {
          this.logger.error('Failed to load orders', err);
          this.loading.set(false);
        }
      });
  }

  onActivePageChange(page: number): void {
    this.currentPage.set(page);
  }

  // ────────────────────────────────────────────────
  // Formatting helpers
  // ────────────────────────────────────────────────
  formatDateTime(dateString: string | null): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatCurrency(amount: number, currency: string = 'USDT'): string {
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
    const symbols: Record<string, string> = { USD: '$', EUR: '€', GBP: '£' };
    // USDT amounts are displayed as USD (1:1)
    const rawCode = (currency || 'USDT').toUpperCase();
    const code = rawCode === 'USDT' ? 'USD' : rawCode;
    const symbol = symbols[code];
    return symbol ? `${symbol}${formatted}` : `${formatted} ${code}`;
  }

  formatStatus(status: string): string {
    const map: Record<string, string> = {
      pending: 'Pending',
      awaiting_confirmation: 'Awaiting Confirmation',
      wallet_connected: 'Wallet Connected',
      awaiting_connection: 'Awaiting Connection',
      processing: 'Processing',
      completed: 'Completed',
      failed: 'Failed',
      cancelled: 'Cancelled'
    };
    return map[status] || status;
  }

  formatPaymentMethod(method: string): string {
    const map: Record<string, string> = {
      ewallet: 'eWallet',
      web3_payment: 'Web3/Crypto',
      credit_card: 'Credit Card',
      direct_fiat: 'Direct Fiat Transfer'
    };
    return map[method] || method;
  }

  getStatusClass(status: string): string {
    const map: Record<string, string> = {
      completed: 'text-success fw-bold',
      processing: 'text-info fw-bold',
      awaiting_confirmation: 'text-warning fw-bold',
      awaiting_connection: 'text-warning fw-bold',
      wallet_connected: 'text-info fw-bold',
      pending: 'text-secondary fw-bold',
      failed: 'text-danger fw-bold',
      cancelled: 'text-muted fw-bold'
    };
    return map[status] || '';
  }

  getStatusBadgeColor(status: string): string {
    const map: Record<string, string> = {
      completed: 'success',
      processing: 'info',
      awaiting_confirmation: 'warning',
      awaiting_connection: 'warning',
      wallet_connected: 'info',
      pending: 'secondary',
      failed: 'danger',
      cancelled: 'dark'
    };
    return map[status?.toLocaleLowerCase()] || 'secondary';
  }

  // ────────────────────────────────────────────────
  // Actions
  // ────────────────────────────────────────────────
  openOrderDetails(orderId: number): void {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    if (isMobile) {
      const lang = this.i18nService.getLanguage() || 'en';
      this.router.navigate([`/${lang}/my-orders`, orderId]);
      return;
    }

    // Desktop: try from cache first
    const cached = this.orders().find(o => o.id === orderId);
    if (cached) {
      this.selectedOrder.set(cached);
      this.detailsModalVisible.set(true);
      return;
    }

    // Fallback: fetch from API
    this.http.get<Order>(`/orders/${orderId}`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: order => {
          this.selectedOrder.set(order);
          this.detailsModalVisible.set(true);
        },
        error: err => {
          this.logger.error('Failed to load order details', err);
          alert('Failed to load order details');
        }
      });
  }

  closeOrderDetails(): void {
    this.detailsModalVisible.set(false);
    this.selectedOrder.set(null);
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text)
      .then(() => this.logger.debug('Copied to clipboard:', text))
      .catch(err => {
        this.logger.error('Failed to copy', err);
        alert('Failed to copy to clipboard');
      });
  }

  downloadBill(orderId: number): void {
    const locale = this.translateService.getActiveLang() || 'en';
    const filename = `Invoice_${orderId}_${new Date().toISOString().split('T')[0]}.pdf`;

    this.downloadService
      .download(`/orders/${orderId}/bill?locale=${locale}`, 
        this.elementRef.nativeElement,
        {
          filename,
          open: true,
          successMessage: `Invoice downloaded for order ${orderId}`,
          errorMessage: 'Failed to download invoice. Please try again.'
        }
      )
      .then(() => this.logger.debug(`Invoice downloaded for order ${orderId}`))
      .catch(err => this.logger.error('Bill download failed', err));
  }

  previewBill(orderId: number): void {
    const locale = this.translateService.getActiveLang() || 'en';

    this.http.get(`/orders/${orderId}/bill/preview?locale=${locale}`, {
      responseType: 'blob',
      observe: 'response'
    })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: response => {
        const blob = response.body;
        if (!blob) {
          this.logger.error('No PDF data for preview');
          alert('Failed to load invoice preview');
          return;
        }

        const url = window.URL.createObjectURL(blob);
        const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        this.billPreviewUrl.set(safeUrl);
        this.billPreviewModalVisible.set(true);
      },
      error: err => {
        this.logger.error('Bill preview failed', err);
        alert('Failed to load invoice preview. Please try again.');
      }
    });
  }

  closeBillPreview(): void {
    const current = this.billPreviewUrl();
    if (current) {
      const url = (current as any).changingThisBreaksApplicationSecurity;
      if (url?.startsWith('blob:')) {
        window.URL.revokeObjectURL(url);
      }
    }
    this.billPreviewModalVisible.set(false);
    this.billPreviewUrl.set(null);
  }

  @HostListener('document:keydown.escape')
  onEscapeKeyPress(): void {
    if (this.billPreviewModalVisible()) this.closeBillPreview();
    if (this.detailsModalVisible()) this.closeOrderDetails();
  }
}